import { boxShadow, Button, mediaHelper, paletteHelper } from '@property.com.au/ui-kit';
import { FunctionComponent } from 'react';
import styled from 'styled-components';
import { SearchPageView } from '../../../../types';

interface SearchRefinementButtonProps {
  activeFilters: number;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  searchView: SearchPageView;
}

const SHOW_ICON_MAX_VIEWPORT_WIDTH = 400;

const StyledButton = styled(Button)<{
  $active: boolean;
  $searchView: SearchPageView;
}>`
  /* Box shadow placed on pseudo element so that it can be given a z-index to appear below sibling components */
  /* See: https://blog.dudak.me/2014/css-shadows-under-adjacent-elements/ */
  &::before {
    ${({ $searchView }) =>
      $searchView === 'map' ? boxShadow.mapBoxShadow : boxShadow.defaultBoxShadow};
    border-radius: inherit;
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1; /* Negative z-index required for box shadow to appear below other elements */
  }

  border: none;
  background-color: ${(props) =>
    props.$active ? paletteHelper('backgroundSelectedBrand') : paletteHelper('backgroundPrimary')};

  &:hover,
  &:focus {
    border: none;
    background-color: ${(props) =>
      props.$active
        ? paletteHelper('backgroundSelectedBrand')
        : paletteHelper('backgroundPrimary')};
    color: ${(props) =>
      props.$active ? paletteHelper('textHoveredBrand') : paletteHelper('textPrimary')};
    ${({ $searchView }) => $searchView === 'list' && boxShadow.boxShadowHover};
  }

  width: 117.5px;
  padding-left: 0;
  padding-right: 0;
  display: flex;
  justify-content: center;
  ${mediaHelper('mobile')} {
    flex: 33.33%;
  }

  @media only screen and (max-width: ${SHOW_ICON_MAX_VIEWPORT_WIDTH}px) {
    svg {
      display: none;
    }
    span {
      margin-left: 0;
    }
  }
`;

const Badge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -6px;
  right: -6px;
  height: 17px;
  width: 17px;
  background-color: ${paletteHelper('backgroundBrandStatic')};
  color: ${paletteHelper('textInverted')};
  border-radius: 50%;
  padding: 4px;
  font-size: 11px;
  font-weight: 500;
  line-height: 15px;
`;

export const SearchRefinementButton: FunctionComponent<SearchRefinementButtonProps> = ({
  activeFilters,
  icon,
  onClick,
  children,
  className,
  searchView,
}) => {
  const isActive = activeFilters > 0;
  return (
    <StyledButton
      aria-pressed={isActive}
      onClick={onClick}
      icon={icon}
      sizeVariant="medium"
      variant={isActive ? 'selected' : 'outline-secondary'}
      $active={isActive}
      dangerouslySetClassNames={{
        label: 'search-refinement-button-label',
      }}
      className={className}
      $searchView={searchView}
    >
      {children}
      {isActive && <Badge data-testid={`${children as string}-badge`}>{activeFilters}</Badge>}
    </StyledButton>
  );
};
